import { vec3 } from "gl-matrix";
import { Face } from "@spi/Common/Geometry";

export class Ray {

    public origin: vec3;
    public direction: vec3;
    public inverseDirection: vec3;

    constructor(near: vec3 | number[], far: vec3 | number[]) {
        this.origin = vec3.copy(vec3.create(), near);

        let direction = vec3.create();
        vec3.subtract(direction, far, near);
        vec3.normalize(direction, direction);

        this.direction = direction;
        this.inverseDirection = vec3.inverse(vec3.create(), direction);
    }

    public getFaceArrayIntersection(faces: Face[]): { face: Face, intersection: vec3 } {

        for (let face of faces) {
            let intersection = this.getFaceIntersection(face);
            if (intersection) {
                return {
                    face: face,
                    intersection: intersection
                }
            }
        }

        return null;
    }


    public getFaceIntersection(face: Face) {
        return this.getPlaneIntersection(
            face.vertices[0].position,
            face.vertices[1].position,
            face.vertices[2].position
        );
    }

    // Returns a vector of the intersection
    public getPlaneIntersection(vecA: vec3 | number[], vecB: vec3 | number[], vecC: vec3 | number[]): vec3 {
        // http://geomalgorithms.com/a06-_intersect-2.html
        // Based on magic (code) from:
        // http://www.lighthouse3d.com/tutorials/maths/ray-triangle-intersection/

        let edgeA = vec3.subtract(vec3.create(), vecB, vecA);
        let edgeB = vec3.subtract(vec3.create(), vecC, vecA);

        let h = vec3.cross(vec3.create(), this.direction, edgeB);
        let a = vec3.dot(edgeA, h);

        if (a > -0.00001 && a < 0.00001) {
            return null;
        }

        let f = 1 / a;
        let s = vec3.subtract(vec3.create(), this.origin, vecA);
        let u = f * (vec3.dot(s, h));

        if (u < 0.0 || u > 1.0) {
            return null;
        }

        let q = vec3.cross(vec3.create(), s, edgeA);
        let v = f * vec3.dot(this.direction, q);

        if (v < 0.0 || u + v > 1.0) {
            return null;
        }

        let t = f * vec3.dot(edgeB, q);
        if (t > 0.00001) { // ray intersection
            return vec3.add(vec3.create(), this.origin, vec3.multiply(vec3.create(), this.direction, [t, t, t]));
        } else {
            return null;
        }
    }

    public intersectsBounds(min: vec3 | number[], max: vec3 | number[]): boolean {
        // This math is beyond me, credit goes to these guys:
        // https://tavianator.com/fast-branchless-raybounding-box-intersections/
        // https://www.cs.utah.edu/~awilliam/box/box.pdf
        // http://www.scratchapixel.com/lessons/3d-basic-rendering/minimal-ray-tracer-rendering-simple-shapes/ray-box-intersection

        var orig = this.origin;
        var dir = this.direction;

        var tmin = (min[0] - orig[0]) / dir[0];
        var tmax = (max[0] - orig[0]) / dir[0];

        if (tmin > tmax) {
            var tmp = tmin;
            tmin = tmax;
            tmax = tmp;
        }

        var tymin = (min[1] - orig[1]) / dir[1];
        var tymax = (max[1] - orig[1]) / dir[1];

        if (tymin > tymax) {
            var tmp = tymin;
            tymin = tymax;
            tymax = tmp;
        }

        if (tmin > tymax || tymin > tmax) {
            return false;
        }

        if (tymin > tmin) {
            tmin = tymin;
        }

        if (tymax < tmax) {
            tmax = tymax;
        }

        var tzmin = (min[2] - orig[2]) / dir[2];
        var tzmax = (max[2] - orig[2]) / dir[2];

        if (tzmin > tzmax) {
            var tmp = tzmin;
            tzmin = tzmax;
            tzmax = tmp;
        }

        if (tmin > tzmax || tzmin > tmax) {
            return false;
        }


        if (tzmin > tmin) {
            tmin = tzmin;
        }

        if (tzmax < tmax) {
            tmax = tzmax;
        }

        return true;
    }

    // https://github.com/stackgl/ray-aabb-intersection/blob/master/index.js
    public getBoundsIntersection(min: vec3 | number[], max: vec3 | number[]): vec3 {
        let distance = this.getBoundsDistance(min, max);
        if (distance === Infinity) {
            return null;
        } else {
            let result = vec3.create();
            for (let i = 0; i < 3; i++) {
                result[i] = this.origin[i] + this.direction[i] * distance;
            }
            return result;
        }
    }

    // https://github.com/stackgl/ray-aabb-intersection/blob/master/index.js
    public getBoundsDistance(min: vec3 | number[], max: vec3 | number[]): number {
        let lo = -Infinity;
        let hi = +Infinity;

        for (let i = 0; i < 3; i++) {
            let dimLo = (min[i] - this.origin[i]) / this.direction[i];
            let dimHi = (max[i] - this.origin[i]) / this.direction[i];

            if (dimLo > dimHi) {
                let tmp = dimLo;
                dimLo = dimHi;
                dimHi = tmp;
            }

            if (dimHi < lo || dimLo > hi) {
                return Infinity;
            }

            if (dimLo > lo) {
                lo = dimLo;
            }
            if (dimHi < hi) {
                hi = dimHi;
            }
        }
        return lo > hi ? Infinity : lo;
    }
}

export interface IIntersectableObject {
    canIntersectRay(): boolean;
    intersectsRay(ray: Ray): boolean;
}

export type RayTraceResult = {
    ray: Ray,
    object: any,
    intersection: any,
    distance: number
}